import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase';

// Define types
export interface UserProfile {
  id: string;
  full_name?: string;
  avatar_url?: string; // Computed field from photos array
  photos?: any[];
  created_at: string;
  updated_at: string;
  gender?: string;
  bio?: string;
  birth_date?: string;
  subscription_status?: string;
  subscription_end_date?: string;
  is_active?: boolean;
}

export interface Match {
  id: string;
  created_at: string;
  room_id: string;
  user1_id: string;
  user2_id: string;
  user1_profile?: UserProfile;
  user2_profile?: UserProfile;
}

export interface PotentialMatch {
  id: string;
  user_id: string;
  room_id: string;
  response: string;
  profiles?: UserProfile;
}

interface MatchState {
  potentialMatches: PotentialMatch[];
  currentMatch: Match | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: MatchState = {
  potentialMatches: [],
  currentMatch: null,
  status: 'idle',
  error: null,
};

// Async thunks
export const getPotentialMatches = createAsyncThunk(
  'match/getPotentialMatches',
  async (roomId: string, {rejectWithValue}) => {
    try {
      // Get all participants who said 'yes'
      const {data, error} = await supabase
        .from('room_participants')
        .select(
          '*, profiles:user_id(id, full_name, photos, created_at, updated_at, gender, bio)',
        )
        .eq('room_id', roomId)
        .eq('response', 'yes');

      if (error) {
        return rejectWithValue(error.message);
      }

      // Process the data to add avatar_url from main photo
      const processedData = (data as PotentialMatch[]).map(match => {
        if (
          match.profiles &&
          match.profiles.photos &&
          Array.isArray(match.profiles.photos) &&
          match.profiles.photos.length > 0
        ) {
          const firstPhoto = match.profiles.photos[0];
          const mainPhotoUrl =
            typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
          return {
            ...match,
            profiles: {
              ...match.profiles,
              avatar_url: mainPhotoUrl || undefined,
            },
          };
        }
        return {
          ...match,
          profiles: match.profiles
            ? {
                ...match.profiles,
                avatar_url: undefined,
              }
            : undefined,
        };
      });

      return processedData || [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to get potential matches');
    }
  },
);

export const checkForMatch = createAsyncThunk(
  'match/checkForMatch',
  async (
    {roomId, userId}: {roomId: string; userId: string},
    {rejectWithValue},
  ) => {
    try {
      // Check if there's a match with this user and room
      const {data, error} = await supabase
        .from('matches')
        .select(
          `
          *,
          room:rooms(*),
          user1_profile:profiles!matches_user1_id_fkey(id, full_name, photos, created_at, updated_at, gender, bio),
          user2_profile:profiles!matches_user2_id_fkey(id, full_name, photos, created_at, updated_at, gender, bio)
        `,
        )
        .eq('room_id', roomId)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

      if (error) {
        return rejectWithValue(error.message);
      }

      // Process the match data to add avatar_url from main photo
      if (data && data.length > 0) {
        const match = data[0] as Match;

        // Process user1_profile
        if (
          match.user1_profile &&
          match.user1_profile.photos &&
          Array.isArray(match.user1_profile.photos) &&
          match.user1_profile.photos.length > 0
        ) {
          const firstPhoto = match.user1_profile.photos[0];
          const mainPhotoUrl =
            typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
          match.user1_profile = {
            ...match.user1_profile,
            avatar_url: mainPhotoUrl || undefined,
          };
        } else if (match.user1_profile) {
          match.user1_profile = {
            ...match.user1_profile,
            avatar_url: undefined,
          };
        }

        // Process user2_profile
        if (
          match.user2_profile &&
          match.user2_profile.photos &&
          Array.isArray(match.user2_profile.photos) &&
          match.user2_profile.photos.length > 0
        ) {
          const firstPhoto = match.user2_profile.photos[0];
          const mainPhotoUrl =
            typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
          match.user2_profile = {
            ...match.user2_profile,
            avatar_url: mainPhotoUrl || undefined,
          };
        } else if (match.user2_profile) {
          match.user2_profile = {
            ...match.user2_profile,
            avatar_url: undefined,
          };
        }

        return match;
      }

      // Return null if no match found
      return null;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to check for match');
    }
  },
);

// Slice
const matchSlice = createSlice({
  name: 'match',
  initialState,
  reducers: {
    setCurrentMatch: (state, action: PayloadAction<Match>) => {
      state.currentMatch = action.payload;
    },
    clearMatchState: state => {
      state.potentialMatches = [];
      state.currentMatch = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Get Potential Matches
      .addCase(getPotentialMatches.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getPotentialMatches.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.potentialMatches = action.payload;
      })
      .addCase(getPotentialMatches.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      // Check for Match
      .addCase(checkForMatch.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentMatch = action.payload;
        }
      });
  },
});

export const {setCurrentMatch, clearMatchState} = matchSlice.actions;
export default matchSlice.reducer;
